import React, { ReactNode } from 'react'
import styled, { css, useTheme } from 'styled-components'
import classNames from 'classnames'
import { darken, lighten } from 'polished'

type ButtonProps = {
  icon?: boolean
  onClick?: () => void
  className?: string
  disabled?: boolean
  displayType?: string
  size?: 'small' | 'normal' | 'medium' | 'large'
  children?: ReactNode
  id?: string
}

const Button: React.FC<ButtonProps> = (props: ButtonProps) => {
  const { className, children, disabled, displayType, ...rest } = props
  return (
    <Container
      disabled={disabled}
      className={classNames('px-button', className)}
      displayType={displayType}
      {...rest}
    >
      <span>{children}</span>
    </Container>
  )
}

export default Button

const buildColor = ({ displayType }: ButtonProps) => {
  const globalTheme = useTheme()

  if (displayType === 'normal') return 'white'
  if (displayType === 'primary') return `${lighten(0.05, globalTheme.colors.primaryAccentColor)}`
  if (displayType === 'secondary') return `${lighten(0.05, globalTheme.colors.grayDarker)}`
  if (displayType === 'danger') return `${lighten(0.05, globalTheme.colors.dangerAccentColor)}`
  return 'white'
}

const buildBorderColor = ({ displayType }: ButtonProps) => {
  const globalTheme = useTheme()

  if (displayType === 'normal') return 'rgb(181, 187, 199)'
  if (displayType === 'primary') return `${lighten(0.05, globalTheme.colors.primaryAccentColor)}`
  if (displayType === 'secondary') return `${lighten(0.05, globalTheme.colors.grayDark)}`
  if (displayType === 'danger') return `${lighten(0.05, globalTheme.colors.dangerAccentColor)}`
  return 'rgb(181, 187, 199)'
}

export const Container = styled.button`
  padding: 0 9px;
  display: inline-block;
  text-align: center;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.4px;
  min-width: 108px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  user-select: none;
  border-radius: 2px;
  height: 36px;
  line-height: 34px;
  position: relative;
  outline: none;
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
  border: 1px solid ${buildBorderColor};
  background-color: ${buildColor};
  color: ${({ theme, displayType }) =>
    displayType === 'normal' ? theme.colors.secondaryHeadlineColor : 'white'};

  ${(props: ButtonProps) =>
    props.size === 'small' &&
    `
    height: 25px;
    line-height: 23px;
    padding: 0 18px;
    min-width: auto;
  `}

  ${(props: ButtonProps) =>
    props.size === 'large' &&
    `
    height: 45px;
    line-height: 43px;
    padding: 0 18px;
  `}

  ${(props: ButtonProps) =>
    props.icon &&
    `
    min-width: initial;
    width: 36px;
  `}

 

  transition: background-color 200ms ease-out, box-shadow 200ms ease-out;

  > span {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    ${({ theme, displayType, disabled }) =>
      !disabled &&
      displayType === 'primary' &&
      ` 
      background: ${darken(0.05, theme.colors.primaryAccentColor)};
        `}
  }

  &:focus {
    ${({ theme, displayType }) =>
      displayType === 'primary' &&
      `
      background: ${darken(0.05, theme.colors.primaryAccentColor)};
    `}

    ${({ theme, displayType }) =>
      displayType === 'secondary' &&
      `
      background: ${darken(0.05, theme.colors.grayDark)};
    `}
    
    ${({ displayType }) =>
      displayType === 'normal' &&
      `
      background: ${darken(0.05, 'white')};
    `}
    
    ${({ theme, displayType }) =>
      displayType === 'danger' &&
      `
      background: ${darken(0.05, theme.colors.dangerAccentColor)};
    `}
  }

  &:active {
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1) inset;
    background-color: ${({ theme }) => darken(0.1, lighten(0.05, theme.colors.primaryAccentColor))};
  }
`

export const ButtonLinkStyles = () => `
  font: inherit;
  color: inherit;
  text-decoration: none;
  border: none;
  background-color: transparent;
  cursor: pointer;
  text-align: inherit;
  padding: 0;
  transition: border 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  outline: none;

  &:disabled,
  &.disabled {
    color: inherit;
    pointer-events: none;
  }
`

export const ButtonLink = styled.button<{ fontSize?: string }>`
  ${ButtonLinkStyles};

  ${({ fontSize }) =>
    fontSize &&
    css`
      font-size: ${fontSize};
    `}
`
